// ================ New ================
import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import AxiosOrgService from '../utils/axiosOrgService';

const extractUsername = (email: string, nickName: string | null | undefined): string => {
  if (!nickName) {
    const parts = email.split("@");
    return parts[0];
  } else {
    return nickName; // Return the nickName if it's not null/undefined
  }
};

const axiosOrgService = new AxiosOrgService();
export interface User {
  id: string;
  nick_name: string;
  avatar: any | null;
  first_name: string;
  last_name: string;
  email: string;
}

export interface Users {
  org_id: string[];
  group_id: string[];
  user_id: string;
  role: string[];
  user: User; // This should be a single User object, not an array
}

export interface Group {
  org_id: string;
  org_name: string;
  name: string;
  id: string;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  users: Users[];
}

export interface Org {
  id: string;
  name: string;
  seat_count: number;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  expired_at: string;
  groups: Group[];
  users: Users[];
}

export interface GroupAdmin {
  org_id: string;
  user: any;
}

export const useOrgStore = defineStore('orgStore', {
  state: () => ({
    orgId: ref<string>(""),
    orgAll: ref<Org[]>([]),
    orgName: ref<string>(""),
    org_role: ref<string>("member"),
    orgSeatCount: ref<number>(-1),
    isOrgAdmin: ref<boolean>(false),
    groupAdminForOrg: ref<GroupAdmin[]>([]),
    groupAll: ref<Group[]>([]),
    isGroupAdmin: ref<boolean>(false),
    memberAll: ref<Users[]>([])
  }),
  actions: {
    async getOrgInfo() {
      try {
        const res = await axiosOrgService.getOrgList(localStorage.token);
        let allGroups:Group[] = [];
        let allMembers: Users[] = [];

        res.data.forEach((org:any) => {
          if (res.data.length > 0) {
            this.org_role = "orgAdmin";
          }

          if (res.data.length === 1) {
            this.orgId = res.data[0].id;
            this.orgName = res.data[0].name;
            this.orgSeatCount = res.data[0].seat_count;
          }

          if (org.groups && org.groups.length > 0) {
            
            this.isOrgAdmin = true;
            const orgGroupsWithOrgName = org.groups.map((group:any) => ({
              ...group,
              org_name: org.name // Add org_name to each group
            }));
            allGroups = allGroups.concat(orgGroupsWithOrgName); 

            org.groups.forEach((group:any) => {
              if (group.users && group.users.length > 0 && group.archived_at === null) {
                // allMembers = allMembers.concat(group.users);
                group.users.forEach((user: any) => {
                  const existingUser = allMembers.find((member) => member.user.id === user.user.id);

                  if (existingUser) {
                    // Concat group_id if user already exists
                    existingUser.group_id = [...new Set([...existingUser.group_id, group.id])];
                    existingUser.role = [...new Set([...existingUser.role, user.role])];
                  } else {
                    // Create new user entry with group_id as an array
                    const username = extractUsername(user.user.email, user.user.nick_name);

                    allMembers.push({
                      org_id: [org.id],
                      group_id: [group.id],
                      user_id: user.user_id,
                      role: [user.role],
                      user: {
                        ...user.user,
                        nick_name: username, // Set the username
                      }
                    });
                  }
                  // group
                  if (user.role === 'GroupAdmin') {
                    const userId = user.user.id;
                    // Check if userId is not already in groupAdminForOrg
                    const exists = this.groupAdminForOrg.some((admin) => admin.user.id === userId);
                    
                    const username = extractUsername(user.user.email, user.user.nick_name);
                    if (!exists) {
                      this.groupAdminForOrg.push({
                        org_id: org.id,
                        user: {
                          ...user.user,
                          nick_name: username,
                        }
                      });
                    }
                  }
                });
              }
            });
          }
        });

        this.orgAll = res.data;
        this.groupAll = allGroups;
        this.memberAll = allMembers;

      } catch (error) {
        console.log("Fail to get OrgInfo!", error);
      }
    },

    async getGroupInfo() {
      try {
        const res = await axiosOrgService.getGroupList(localStorage.token);

        let allGroups: Group[] = [];
        let allMembers: Users[] = [];

        if (res.data.length > 0) {
          this.org_role = "groupAdmin";
          this.orgId = res.data[0].org_id;
        }

        if (res.data.length === 1) {
          this.isGroupAdmin = true;
          this.groupAll = res.data;
          this.memberAll = res.data[0].users;

        } else if (res.data.length > 1) {
          this.groupAll = res.data;

          res.data.forEach((group: any) => {
            if (group.users && group.users.length > 0 && group.archived_at === null) {
              group.users.forEach((user: any) => {
                const existingUser = allMembers.find((member) => member.user.id === user.user.id);

                if (existingUser) {
                  existingUser.group_id = [...new Set([...existingUser.group_id, group.id])];
                  existingUser.role = [...new Set([...existingUser.role, user.role])];
                } else {
                  const username = extractUsername(user.user.email, user.user.nick_name);

                  allMembers.push({
                    org_id: [group.org_id],
                    group_id: [group.id],
                    user_id: user.user_id,
                    role: [user.role],
                    user: {
                      ...user.user,
                      nick_name: username, // Set the username
                    }
                  });
                }

                if (user.role === 'GroupAdmin') {
                  const userId = user.user.id;
                  const exists = this.groupAdminForOrg.some((admin) => admin.user.id === userId);
                  const username = extractUsername(user.user.email, user.user.nick_name);

                  if (!exists) {
                    this.groupAdminForOrg.push({
                      org_id: group.org_id,
                      user: {
                        ...user.user,
                        nick_name: username,
                      }
                    });
                  }
                }
              });
            }
          });
          this.memberAll = allMembers;
        }
      } catch (error) {
        console.log("Failed to get GroupInfo!", error);
      }
    },

    async getAllInfo () {
      await this.getOrgInfo();
      if (this.orgAll.length === 0) {
        await this.getGroupInfo();
      }
      console.log("Final-OrgAll: ", this.orgAll)
      console.log("Final-GroupAll: ", this.groupAll)
      console.log("Final-memberAll: ", this.memberAll)
      console.log("Final-role: ", this.org_role)
    },
    
  },
  getters: {
    activeGroupAll: (state) => {
      return state.groupAll.filter(group => group.archived_at === null);
    },
    getUpdatedGroupAll: (state) => state.groupAll,
  },
});